import { matchesTokens } from "@/lib/search";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumber } from "@/lib/format";
import { Card, PageHeader, Badge, Modal, Button, Input, Select } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";
import FilePreview from "@/components/FilePreview";
import { Truck, ChevronLeft, Image as ImageIcon, Eye, Download, Check, X as XIcon, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { DatePicker } from "@/components/DatePicker";

// Iter v34br (Task 1) — Status Tagihan badge (READY/DONE/UNBILLED) — warna sama dgn portal admin.
function StatusTagihanBadge({ sj, size = "md" }) {
  const count = parseInt(sj.billed_count || 0, 10);
  const legacy = sj.billed_invoice_id ? 1 : 0;
  const effective = Math.max(count, legacy);
  const isBilled = effective > 0;
  const isReady = !isBilled && !!sj.photo_after_id;
  const cls = size === "sm" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  if (isBilled) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase ${cls}`}
            style={{ backgroundColor: "#53db1e", borderColor: "#43b016", color: "#000" }} data-testid={`client-sj-status-billed-${sj.sj_number}`}>
        <Check className="h-3 w-3"/> Done
      </span>
    );
  }
  if (isReady) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full bg-yellow-300 text-yellow-950 border border-yellow-500 font-bold uppercase ${cls}`} data-testid={`client-sj-status-ready-${sj.sj_number}`}>
        <Check className="h-3 w-3"/> Ready
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-500 border border-stone-200 font-bold uppercase ${cls}`} data-testid={`client-sj-status-unbilled-${sj.sj_number}`}>
      <XIcon className="h-3 w-3"/> Belum
    </span>
  );
}

/**
 * ClientSuratJalan — full SJ list for the logged-in klien.
 * Iter v34bp — Tambah tombol Detail per row → membuka Modal detail SJ.
 * Di dalam Detail Modal, klien bisa melihat items, sopir, truk, PO,
 * plus 2 tombol Foto (Sebelum Kirim + Sesudah Kirim) yang membuka
 * FilePreview di atas modal detail (z-[60] via prop `topmost`).
 * FilePreview memiliki tombol Unduh untuk download langsung.
 */
export default function ClientSuratJalan() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailSj, setDetailSj] = useState(null);
  const [previewFile, setPreviewFile] = useState(null); // { id, name, ct }

  useEffect(() => {
    setLoading(true);
    api.get("/client/surat-jalan")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // Iter v34bv (#11) — Search + Filter untuk Portal Klien SJ.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | unbilled | ready | billed
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filtered = useMemo(() => {
    // Iter v34cd (#1) — Wrap dgn try/catch supaya nilai DatePicker aneh tidak crash blank layar.
    try {
      const q = (search || "").trim().toLowerCase();
      const startStr = startDate ? String(startDate).slice(0, 10) : "";
      const endStr = endDate ? String(endDate).slice(0, 10) : "";
      return (rows || []).filter((sj) => {
        if (!sj) return false;
        if (q) {
          // Iter v34cw (#5) — pencarian kombinasi kata (semua token harus ada).
          const hay = [
            sj.sj_number, sj.vehicle_no, sj.delivery_location, sj.po_number, sj.driver, sj.date, sj.category,
            ...((sj.items || []).map((it) => (it && it.material_name) || "")),
          ].join(" ");
          if (!matchesTokens(hay, q)) return false;
        }
        if (statusFilter !== "all") {
          const count = parseInt(sj.billed_count || 0, 10);
          const legacy = sj.billed_invoice_id ? 1 : 0;
          const effective = Math.max(count, legacy);
          const isBilled = effective > 0;
          const isReady = !isBilled && !!sj.photo_after_id;
          const currentStatus = isBilled ? "billed" : isReady ? "ready" : "unbilled";
          if (currentStatus !== statusFilter) return false;
        }
        const sjDate = String(sj.date || "").slice(0, 10);
        if (startStr && sjDate < startStr) return false;
        if (endStr && sjDate > endStr) return false;
        return true;
      });
    } catch (e) {
      console.error("ClientSJ filter error:", e);
      return rows || [];
    }
  }, [rows, search, statusFilter, startDate, endDate]);

  const pagi = usePagination(filtered, { perPage: 14 });

  return (
    <div className="min-h-full bg-[#fafaf5]">
      <div className="max-w-[1200px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <Link to="/client" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-500 hover:text-blue-700">
          <ChevronLeft className="h-3 w-3" /> Beranda
        </Link>
        <PageHeader
          overline={`Klien · ${user?.client_name || "—"}`}
          title="Riwayat Surat Jalan"
          description="Daftar lengkap Surat Jalan pengiriman atas nama perusahaan Anda."
        />

        {/* Iter v34bv (#11) — Search + Filter */}
        <Card className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari kode SJ / material / lokasi / kendaraan…" className="pl-9" data-testid="client-sj-search" />
            </div>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-w-[160px]" data-testid="client-sj-status-filter">
              <option value="all">Semua Status Tagihan</option>
              <option value="unbilled">Belum Ditagih</option>
              <option value="ready">Siap Ditagih (Ready)</option>
              <option value="billed">Sudah Ditagih</option>
            </Select>
            <div className="w-[150px]"><DatePicker value={startDate} onChange={setStartDate} placeholder="Dari tanggal" data-testid="client-sj-start-date" /></div>
            <div className="w-[150px]"><DatePicker value={endDate} onChange={setEndDate} placeholder="Sampai tanggal" data-testid="client-sj-end-date" /></div>
            {(search || statusFilter !== "all" || startDate || endDate) && (
              <Button variant="ghost" onClick={() => { setSearch(""); setStatusFilter("all"); setStartDate(""); setEndDate(""); }} data-testid="client-sj-reset-filter">Reset</Button>
            )}
            <span className="text-xs text-stone-500">Menampilkan {filtered.length} dari {rows.length}</span>
          </div>
        </Card>

        <Card className="p-0" data-testid="client-sj-list">
          {loading ? (
            <p className="px-5 py-16 text-center text-sm text-stone-400">Memuat…</p>
          ) : filtered.length === 0 ? (
            <p className="px-5 py-16 text-center text-sm text-stone-400">{rows.length === 0 ? "Belum ada Surat Jalan." : "Tidak ada SJ yang cocok dengan filter."}</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {pagi.pageItems.map((sj) => (
                <div key={sj.id} className="px-5 py-3.5 hover:bg-stone-50/50" data-testid={`client-sj-row-${sj.sj_number}`}>
                  <div className="flex items-start gap-3">
                    <Truck className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[11px] text-stone-500 font-mono">{sj.sj_number}</p>
                        <Badge tone="stone">{sj.date}</Badge>
                        {sj.file_id && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 border border-blue-200">
                            <ImageIcon className="h-2.5 w-2.5" /> Foto Sebelum
                          </span>
                        )}
                        {sj.photo_after_id && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 border border-emerald-200">
                            <ImageIcon className="h-2.5 w-2.5" /> Foto Sesudah
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-stone-900 truncate mt-0.5">{sj.delivery_location || "—"}</p>
                      <p className="text-[11px] text-stone-500 mt-0.5">{sj.vehicle_no || "—"} · {sj.driver || "—"}</p>
                      {(sj.items || []).length > 0 && (
                        <p className="text-[11px] text-stone-600 mt-1">
                          {sj.items.slice(0, 3).map((it) => `${it.material_name} (${formatNumber(it.quantity || 0, 2)} ${it.unit})`).join(", ")}
                          {sj.items.length > 3 ? ` +${sj.items.length - 3}` : ""}
                        </p>
                      )}
                    </div>
                    {/* Iter v34br (Task 1) — Status Tagihan badge di kiri tombol Detail */}
                    <div className="flex items-center gap-2 self-center shrink-0">
                      <StatusTagihanBadge sj={sj}/>
                      <button
                        onClick={() => setDetailSj(sj)}
                        className="text-stone-500 hover:text-blue-700 px-2 inline-flex items-center gap-1 text-xs uppercase tracking-wider border border-stone-200 py-1 hover:bg-blue-50 hover:border-blue-300"
                        data-testid={`client-sj-detail-${sj.sj_number}`}
                        title="Lihat detail Surat Jalan"
                      >
                        <Eye className="h-3.5 w-3.5" /> Detail
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        {pagi.Pagination}
      </div>

      {/* Iter v34bp — Detail Modal (z-50) */}
      <Modal open={!!detailSj} onClose={() => setDetailSj(null)} title={detailSj ? `Detail Surat Jalan · ${detailSj.sj_number}` : ""} size="lg">
        {detailSj && (
          <div className="space-y-4 text-sm" data-testid="client-sj-detail-modal">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Meta label="No. SJ"><span className="font-mono font-medium">{detailSj.sj_number}</span></Meta>
              <Meta label="Tanggal"><span className="font-mono">{detailSj.date}</span></Meta>
              <Meta label="Nama Klien">{detailSj.client_name || "—"}</Meta>
              <Meta label="Nomor PO">{detailSj.po_number || "—"}</Meta>
              <Meta label="Lokasi Pengiriman">{detailSj.delivery_location || "—"}</Meta>
              <Meta label="No. Kendaraan"><span className="font-mono">{detailSj.vehicle_no || "—"}</span></Meta>
              {/* Iter v34br (Task 1) — Ganti "Sopir" dengan "Status Tagihan" (badge yang sama dgn portal admin). */}
              <Meta label="Status Tagihan"><StatusTagihanBadge sj={detailSj}/></Meta>
              {detailSj.notes && <Meta label="Catatan">{detailSj.notes}</Meta>}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Rincian Item</div>
              <div className="border border-stone-200">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
                    <tr>
                      <th className="text-left py-2 px-3">Material</th>
                      <th className="text-right py-2 px-3">Qty</th>
                      <th className="text-left py-2 px-3">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailSj.items && detailSj.items.length > 0) ? detailSj.items.map((it, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="py-2 px-3">{it.material_name || "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatNumber(it.quantity || 0, 2)}</td>
                        <td className="py-2 px-3">{it.unit || "—"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={3} className="py-4 px-3 text-center text-stone-400 italic">Belum ada item.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Lampiran Foto</div>
              <div className="flex flex-wrap gap-2">
                {detailSj.file_id && (
                  <button
                    onClick={() => setPreviewFile({ id: detailSj.file_id, name: detailSj.file_name || `foto-sebelum-${detailSj.sj_number}`, ct: detailSj.file_content_type || "image/jpeg" })}
                    className="inline-flex items-center gap-1 border border-stone-300 px-3 py-1.5 hover:bg-blue-50 hover:border-blue-300 text-xs"
                    data-testid="client-sj-detail-photo-before"
                  >
                    <Eye className="h-3 w-3 text-blue-700"/> Foto Sebelum Kirim
                  </button>
                )}
                {detailSj.photo_after_id && (
                  <button
                    onClick={() => setPreviewFile({ id: detailSj.photo_after_id, name: detailSj.photo_after_name || `foto-sesudah-${detailSj.sj_number}`, ct: detailSj.photo_after_content_type || "image/jpeg" })}
                    className="inline-flex items-center gap-1 border border-stone-300 px-3 py-1.5 hover:bg-emerald-50 hover:border-emerald-300 text-xs"
                    data-testid="client-sj-detail-photo-after"
                  >
                    <Eye className="h-3 w-3 text-emerald-700"/> Foto Sesudah Kirim
                  </button>
                )}
                {!detailSj.file_id && !detailSj.photo_after_id && (
                  <span className="text-xs text-stone-400 italic">Belum ada lampiran foto.</span>
                )}
                {/* Iter v34bp — Direct download links (tanpa buka preview) */}
                {detailSj.file_id && (
                  <a
                    href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/${detailSj.file_id}?download=1`}
                    className="inline-flex items-center gap-1 border border-stone-300 px-3 py-1.5 hover:bg-stone-50 text-xs"
                    data-testid="client-sj-download-before"
                    title="Unduh foto sebelum"
                  >
                    <Download className="h-3 w-3"/> Unduh Sebelum
                  </a>
                )}
                {detailSj.photo_after_id && (
                  <a
                    href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/${detailSj.photo_after_id}?download=1`}
                    className="inline-flex items-center gap-1 border border-stone-300 px-3 py-1.5 hover:bg-stone-50 text-xs"
                    data-testid="client-sj-download-after"
                    title="Unduh foto sesudah"
                  >
                    <Download className="h-3 w-3"/> Unduh Sesudah
                  </a>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setDetailSj(null)}>Tutup</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v34bp — File Preview (z-[60], di atas Detail Modal). Punya tombol Unduh built-in. */}
      <FilePreview
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        fileId={previewFile?.id}
        filename={previewFile?.name}
        contentType={previewFile?.ct}
        topmost={!!detailSj}
      />
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
